/**
 * aico-neo-entity.js
 *
 * Paints AICO neo documents attached to a PRODUCT or a COLLECTION, using the
 * shared engine in assets/content-builder-neo.js (window.NewsNeo). Markup comes
 * from snippets/aico-neo-entity-content.liquid; see that file for the metafield
 * contract.
 *
 * Per mount ([data-aico-neo-mount]):
 *   1. Take the inline document (custom_fields.content_builder_json, shaped
 *      {"contentBuilderNeoJson": {...raw doc...}}). When it is absent (AICO
 *      omits it above Shopify's 64 KB value cap, or when the file did not
 *      resolve), fetch custom_fields.content_builder_url instead (the raw doc).
 *   2. Apply the visitor's language (NewsNeo.resolveLocale): neither source is
 *      locale-resolved by AICO. Then sanitise every markup string with
 *      DOMPurify (assets/purify.min.js); without DOMPurify nothing is painted.
 *   3. Render. With data-neo-section-titles, each section is preceded by the
 *      legacy `.row-translated-title[data-title]` marker the product page
 *      sidebar navigation (main-product.js) is built from.
 *   4. On any failure or an empty document, put the legacy markup kept in
 *      <template data-aico-neo-fallback> in its place, so the visitor sees
 *      exactly what the theme showed before neo existed. If that is empty too
 *      and the tab exists only because of neo, the tab is withdrawn.
 *
 * Every mount dispatches `aico-neo:entity-rendered` on document when it is
 * settled (detail: {mount, outcome: 'neo' | 'fallback' | 'empty'}), and
 * window.aicoNeoEntityPending counts mounts not yet settled, so page scripts
 * that read the rendered DOM can wait for it.
 */
(function (window, document) {
  'use strict';

  var GENERIC_SECTION_NAMES = { '': true, 'section': true };

  function escapeAttr(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function readInline(mount) {
    var id = mount.getAttribute('data-neo-data-id');
    var el = id ? document.getElementById(id) : null;
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch (e) {
      if (window.console && console.warn) console.warn('aico-neo-entity: bad inline JSON', e);
      return null;
    }
  }

  // Accept both the wrapped metafield shape and the raw file shape.
  function unwrap(payload) {
    if (!payload || typeof payload !== 'object') return null;
    var doc = payload.contentBuilderNeoJson ? payload.contentBuilderNeoJson : payload;
    return doc && typeof doc === 'object' ? doc : null;
  }

  function sectionName(section) {
    var name = section && typeof section.name === 'string' ? section.name.trim() : '';
    return GENERIC_SECTION_NAMES[name.toLowerCase()] ? '' : name;
  }

  // Every markup-bearing string in an element's content (text, heading, html,
  // table cells, captions, ...) goes through DOMPurify before the engine prints
  // it. Same policy as the news path (snippets/news-script.liquid on
  // feature/neo-renderer-news): iframes stay (video embeds), scripts, on*
  // handlers and javascript: URLs do not. Strings without '<' pass unchanged.
  function sanitizeValue(value) {
    if (typeof value === 'string') {
      if (value.indexOf('<') === -1) return value;
      return window.DOMPurify.sanitize(value, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'target']
      });
    }
    if (Array.isArray(value)) return value.map(sanitizeValue);
    if (value && typeof value === 'object') {
      var out = {};
      for (var key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) out[key] = sanitizeValue(value[key]);
      }
      return out;
    }
    return value;
  }

  function sanitizeElements(elements) {
    if (!Array.isArray(elements)) return elements;
    return elements.map(function (element) {
      if (!element || typeof element !== 'object') return element;
      var copy = {};
      for (var key in element) {
        if (Object.prototype.hasOwnProperty.call(element, key)) copy[key] = element[key];
      }
      if (copy.content && typeof copy.content === 'object') copy.content = sanitizeValue(copy.content);
      if (Array.isArray(copy.children)) copy.children = sanitizeElements(copy.children);
      if (Array.isArray(copy.elements)) copy.elements = sanitizeElements(copy.elements);
      return copy;
    });
  }

  function renderDocument(mount, doc) {
    var Neo = window.NewsNeo;
    var locale = mount.getAttribute('data-neo-locale') || '';
    var defaultLocale = mount.getAttribute('data-neo-default-locale') || null;
    if (typeof Neo.resolveLocale === 'function') doc = Neo.resolveLocale(doc, locale, defaultLocale);
    if (Array.isArray(doc.sections)) {
      doc.sections = doc.sections.map(function (section) {
        if (!section || typeof section !== 'object') return section;
        var copy = {};
        for (var key in section) {
          if (Object.prototype.hasOwnProperty.call(section, key)) copy[key] = section[key];
        }
        copy.elements = sanitizeElements(section.elements);
        return copy;
      });
    }

    var data = { contentBuilderNeoJson: doc };
    if (!Neo.hasContent(data)) return '';

    // A document whose elements carry their legacy block (aico --legacy-block-cards) is drawn as
    // the legacy cards; the tab's stylesheets scope the card rules to the legacy container's
    // classes (`.content-builder-content.content-builder-json .content-column .card`), so the
    // mount takes them too.
    if (typeof Neo.hasLegacyCards === 'function' && Neo.hasLegacyCards(data)) {
      mount.className += ' content-builder-content content-builder-json';
    }

    // data-neo-theme-tables (product tab): plain tables come out as the legacy
    // builder's bare `figure.table > table`, so the tab's own table styles
    // draw them as they draw the legacy ones.
    var options = { themeTables: mount.hasAttribute('data-neo-theme-tables') };

    var html = renderSections(Neo, doc, data, options, mount);
    // data-neo-plain-category-names: the theme's legacy category list prints each category's name as
    // plain text (infrarotheizung-shop.ch and luftentfeuchter-shop.ch: the link branch of
    // content_builders_json.liquid is switched off with `and false`), so the name is drawn without
    // the link the document gives it.
    if (html && mount.hasAttribute('data-neo-plain-category-names')) html = withPlainCategoryNames(html);
    return html;
  }

  function withPlainCategoryNames(html) {
    if (!document.createElement) return html;
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    var links = tpl.content.querySelectorAll('.content-type-sub_sub_category .text-section h3 a');
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      while (link.firstChild) link.parentNode.insertBefore(link.firstChild, link);
      link.parentNode.removeChild(link);
    }
    return links.length ? tpl.innerHTML : html;
  }

  function renderSections(Neo, doc, data, options, mount) {
    if (!mount.hasAttribute('data-neo-section-titles')) return Neo.render(data, options);
    var everyRow = mount.hasAttribute('data-neo-row-markers');

    var html = '';
    for (var i = 0; i < doc.sections.length; i++) {
      var section = doc.sections[i];
      var body = Neo.render({ contentBuilderNeoJson: { sections: [section] } }, options);
      if (!body) continue;
      var title = sectionName(section);
      if (everyRow) {
        html += withRowMarkers(body, title);
        continue;
      }
      if (title) {
        html += rowMarker(title);
      }
      html += body;
    }
    return html;
  }

  function rowMarker(title) {
    return '<div class="row-translated-title" data-title="' + escapeAttr(title) + '">&nbsp;</div>';
  }

  // data-neo-row-markers (with data-neo-section-titles): the theme's legacy builder snippet prints the
  // `.row-translated-title` marker before EVERY row, titled or not (an untitled row gets data-title=""),
  // and the marker is a line of the tab (its &nbsp; takes a line's height). A neo section holds several
  // rows, so the section title alone left out the line before each further row. Here every top-level
  // row of the section gets its marker, the first one carrying the section title, as the legacy rows did.
  function withRowMarkers(body, title) {
    if (!document.createElement) return rowMarker(title) + body;
    var tpl = document.createElement('template');
    tpl.innerHTML = body;
    var root = tpl.content;
    var first = true;
    var nodes = Array.prototype.slice.call(root.childNodes);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType !== 1 || !node.classList || !node.classList.contains('content-row')) continue;
      var marker = document.createElement('div');
      marker.className = 'row-translated-title';
      marker.setAttribute('data-title', first ? title : '');
      marker.innerHTML = '&nbsp;';
      root.insertBefore(marker, node);
      first = false;
    }
    if (first) return rowMarker(title) + body;
    return tpl.innerHTML;
  }

  function withdrawTab(mount) {
    var tabId = mount.getAttribute('data-neo-tab-only');
    if (!tabId) return;
    var link = document.querySelector('.tab-link[data-tab="' + tabId + '"]');
    if (link && link.parentNode) link.parentNode.removeChild(link);
    var pane = document.getElementById(tabId);
    if (pane) pane.classList.remove('current');
    var next = document.querySelector('.tab-link[data-tab]');
    if (next) {
      next.classList.add('current');
      var nextPane = document.getElementById(next.getAttribute('data-tab'));
      if (nextPane) nextPane.classList.add('current');
    }
  }

  function settle(mount, outcome) {
    mount.removeAttribute('data-aico-neo-pending');
    mount.setAttribute('data-aico-neo-outcome', outcome);
    window.aicoNeoEntityPending = Math.max(0, (window.aicoNeoEntityPending || 1) - 1);
    var evt;
    try {
      evt = new CustomEvent('aico-neo:entity-rendered', { detail: { mount: mount, outcome: outcome } });
    } catch (e) {
      evt = document.createEvent('CustomEvent');
      evt.initCustomEvent('aico-neo:entity-rendered', false, false, { mount: mount, outcome: outcome });
    }
    document.dispatchEvent(evt);
  }

  function fallback(mount) {
    var tpl = mount.parentNode && mount.parentNode.querySelector('template[data-aico-neo-fallback]');
    var legacy = tpl ? tpl.innerHTML.trim() : '';
    if (legacy) {
      mount.insertAdjacentHTML('afterend', tpl.innerHTML);
      mount.parentNode.removeChild(mount);
      settle(mount, 'fallback');
      return;
    }
    withdrawTab(mount);
    settle(mount, 'empty');
  }

  // A legacy card draws a Wistia video as the legacy block embedded it (the library's
  // `.wistia_embed` box); the library that turns the box into the player is loaded once, and
  // only when such a box was drawn. Its own address, as the legacy block wrote it.
  function loadWistiaLibrary(mount) {
    if (!mount.querySelector('.wistia_embed')) return;
    var src = 'https://fast.wistia.com/assets/external/E-v1.js';
    if (document.querySelector('script[src="' + src + '"]')) return;
    var script = document.createElement('script');
    script.src = src;
    script.async = true;
    document.head.appendChild(script);
  }

  function paint(mount, doc) {
    var html = '';
    try {
      html = doc ? renderDocument(mount, doc) : '';
    } catch (e) {
      if (window.console && console.warn) console.warn('aico-neo-entity: render failed', e);
      html = '';
    }
    if (!html) {
      fallback(mount);
      return;
    }
    mount.innerHTML = html;
    window.NewsNeo.hydrate(mount);
    loadWistiaLibrary(mount);
    settle(mount, 'neo');
  }

  function boot(mount) {
    // Fail closed: without the sanitiser the legacy output stays.
    if (!window.NewsNeo || !window.DOMPurify) {
      fallback(mount);
      return;
    }
    var inline = unwrap(readInline(mount));
    if (inline && window.NewsNeo.hasContent({ contentBuilderNeoJson: inline })) {
      paint(mount, inline);
      return;
    }
    var url = mount.getAttribute('data-neo-url');
    if (!url || typeof fetch !== 'function') {
      fallback(mount);
      return;
    }
    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (payload) { paint(mount, unwrap(payload)); })
      .catch(function (e) {
        if (window.console && console.warn) console.warn('aico-neo-entity: fetch failed', url, e);
        fallback(mount);
      });
  }

  function start() {
    var mounts = document.querySelectorAll('[data-aico-neo-mount][data-aico-neo-pending]');
    window.aicoNeoEntityPending = (window.aicoNeoEntityPending || 0) + mounts.length;
    for (var i = 0; i < mounts.length; i++) boot(mounts[i]);
  }

  // Deferred: by now the DOM is parsed and the engine (included before this
  // file) has run, and DOMContentLoaded (jQuery ready) has not fired yet, so an
  // inline document is painted before main-product.js reads the page.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})(window, document);
